/**
 * Feature 4 (2026-05-28) - AgreementTasksTab.
 *
 * Tasks tab on AgreementDetailPage. Status filter is a row of always-
 * visible toggle pills (Open / Done / Cancelled / Deleted) — green when
 * active, grey when not. Overdue OPEN rows pin to the top (server-side, under every
 * header sort — P41 phase 2).
 */
import {
  Badge, Button, Chip, Group, MultiSelect, Pagination, Stack, Table, Text,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import * as React from 'react';

import { useCurrentUser } from '../hooks/useAuth';
import { useAgreementTasks } from '../hooks/useAgreementTasks';
import type {
  AgreementTask, AgreementTaskStatus, UUID,
} from '../api/types';
import { AGREEMENT_TASK_STATUS_LABELS } from '../api/types';
import { TaskFormModal } from './TaskFormModal';
import { TaskRowActions } from './TaskRowActions';
import { PendingCRBadge } from './PendingCRBadge';
import { isOverdue, type TaskSortKey } from '../utils/taskList';
import { URGENCY_OPTIONS, urgencyColor, urgencyLabel } from '../utils/taskUrgency';
import { SortableTh, nextSort, sortParam, type SortState } from './SortableHeader';
import { StatusExplained } from './StatusExplained';

const PAGE_SIZE = 50;

interface Props {
  agreementId: UUID;
}

const STATUS_COLOR: Record<AgreementTaskStatus, string> = {
  OPEN: 'yellow',
  DONE: 'green',
  CANCELLED: 'gray',
};

// "DELETED" is a synthetic chip value that toggles include_deleted; it
// is not a real status enum but lets the four chips share one component.
type FilterChip = AgreementTaskStatus | 'DELETED';

export function AgreementTasksTab({ agreementId }: Props) {
  const { user } = useCurrentUser();
  const isChecker = user?.role === 'admin' || user?.role === 'compliance';

  // Default: Open only. Checker can also tick Deleted to surface soft-deleted.
  const [selected, setSelected] = React.useState<FilterChip[]>(['OPEN']);
  // P41 phase 2 (2026-10-15): every header sorts, server-side across pages; null = the
  // existing order (deadline, earliest first). Overdue open tasks always lead (P14).
  const [sort, setSortState] = React.useState<SortState<TaskSortKey> | null>(null);
  const [page, setPage] = React.useState(1);
  function onSort(k: TaskSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }
  const [urgencyF, setUrgencyF] = React.useState<string[]>([]);

  const statuses = selected.filter((s) => s !== 'DELETED') as AgreementTaskStatus[];
  const includeDeleted = isChecker && selected.includes('DELETED');

  const q = useAgreementTasks(agreementId, {
    statuses: statuses.length > 0 ? statuses : undefined,
    include_deleted: includeDeleted,
    urgency: urgencyF.length > 0 ? urgencyF.map(Number) : undefined,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AgreementTask | null>(null);

  const items = q.data?.items ?? [];
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE)) : 1;
  const filterKey = JSON.stringify([selected, urgencyF]);
  React.useEffect(() => { setPage(1); }, [filterKey]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  // The Edit affordance is gated inside TaskRowActions (the creator or an
  // Admin), so only they can reach this — just open the modal.
  function openEdit(task: AgreementTask) {
    setEditing(task);
    setModalOpen(true);
  }

  return (
    <Stack>
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <Text size="sm" c="dimmed">
          Tasks for this Agreement. Deadlines, assignees, and status are tracked here;
          the dashboard "My Tasks" card aggregates across all Agreements.
        </Text>
        <Group gap="md" wrap="nowrap">
          <Button size="sm" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            New task
          </Button>
        </Group>
      </Group>

      <Group gap="lg" wrap="wrap" align="flex-end">
        <Stack gap={4}>
          <Text size="sm" fw={500}>Status</Text>
          <Chip.Group multiple value={selected as string[]}
            onChange={(v) => setSelected(v as FilterChip[])}>
            <Group gap="xs">
              {/* P47: each status explains itself on hover. */}
              <StatusExplained entity="task" status="OPEN"><Chip value="OPEN" color="green">Open</Chip></StatusExplained>
              <StatusExplained entity="task" status="DONE"><Chip value="DONE" color="green">Done</Chip></StatusExplained>
              <StatusExplained entity="task" status="CANCELLED"><Chip value="CANCELLED" color="green">Cancelled</Chip></StatusExplained>
              {isChecker && (
                <StatusExplained entity="task" status="DELETED"><Chip value="DELETED" color="green">Deleted</Chip></StatusExplained>
              )}
            </Group>
          </Chip.Group>
        </Stack>
        <MultiSelect
          label="Urgency"
          placeholder={urgencyF.length ? undefined : 'Any'}
          data={URGENCY_OPTIONS}
          value={urgencyF}
          onChange={setUrgencyF}
          clearable
          comboboxProps={{ withinPortal: true }}
          w={240}
        />
      </Group>

      {q.isLoading && <Text c="dimmed" size="sm">Loading…</Text>}
      {!q.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm">
          No tasks for the current filter. Click "New task" to create one.
        </Text>
      )}

      {items.length > 0 && (
        <Table withTableBorder striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {/* P41 phase 2 (2026-10-15): every header sorts (server-side). */}
              <SortableTh label="Deadline" col="deadline" sort={sort} onSort={onSort} w={120} />
              <SortableTh label="Urgency" col="urgency" sort={sort} onSort={onSort} w={110}
                filtered={urgencyF.length > 0} />
              <SortableTh label="Description" col="description" sort={sort} onSort={onSort} />
              <SortableTh label="Assignee" col="assignee" sort={sort} onSort={onSort} w={180} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={120} />
              <SortableTh label="Created" col="created" sort={sort} onSort={onSort} w={170} />
              <Table.Th w={140}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((task) => {
              const isDeleted = !!task.deleted_at;
              const overdue = isOverdue(task);
              return (
                <Table.Tr key={task.id}
                  style={isDeleted ? { opacity: 0.55 } : undefined}>
                  <Table.Td>
                    <Text size="sm" ff="monospace"
                      c={overdue ? 'red' : undefined}
                      fw={overdue ? 600 : undefined}
                      td={isDeleted ? 'line-through' : undefined}>
                      {task.deadline}
                    </Text>
                    {overdue && (
                      <Text size="xs" c="red" fw={500}>Overdue</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={urgencyColor(task.urgency)} variant="light">
                      {urgencyLabel(task.urgency)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" td={isDeleted ? 'line-through' : undefined}>
                      {task.description}
                    </Text>
                    {isDeleted && (
                      <StatusExplained entity="task" status="DELETED">
                        <Badge color="red" variant="light" size="sm" mt={4}>Deleted</Badge>
                      </StatusExplained>
                    )}
                    {/* B1/#35 (2026-06-11, old #364): pending-CR badge on
                        lower-traffic lists too. */}
                    <PendingCRBadge crs={task.pending_change_requests} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{task.assignee_label ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusExplained entity="task" status={task.status}>
                      <Badge color={STATUS_COLOR[task.status]} variant="light">
                        {AGREEMENT_TASK_STATUS_LABELS[task.status] ?? task.status}
                      </Badge>
                    </StatusExplained>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{task.created_by_label ?? '—'}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {task.created_at.substring(0, 10)}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <TaskRowActions task={task} onEdit={openEdit} />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      {q.data && q.data.total > PAGE_SIZE && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{q.data.total} total</Text>
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}

      <TaskFormModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        agreementId={agreementId}
        editing={editing}
      />
    </Stack>
  );
}
